/**
 * BSGOTECH — "One Ecosystem" institutional section.
 *
 * Placed on the homepage RIGHT AFTER the Hero (no other section is touched).
 * Displays the corporate architecture:
 *   BSGOTECH  →  NeuNexus  →  (RENALIX | NeuGreen Energy)
 *
 * Also used inside the dedicated `/our-ecosystem` page.
 * Fully DE / EN / PT via shared LangCtx.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, HeartPulse, SunMedium, ChevronDown, ArrowUpRight } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  white:   "#FFFFFF",
  green:   "#22C55E",
  teal:    "#14B8A6",
  rose:    "#F43F5E",
  blue:    "#3B82F6",
  purple:  "#A855F7",
};

const DATA = {
  en: {
    eyebrow: "Corporate Architecture",
    title: "One Ecosystem. Multiple Intelligent Solutions.",
    lead: [
      { name: "BSGOTECH",       desc: "develops intelligent digital ecosystems." },
      { name: "NeuNexus",       desc: "integrates technologies." },
      { name: "RENALIX",        desc: "transforms healthcare." },
      { name: "NeuGreen Energy", desc: "powers sustainable infrastructure." },
    ],
    parent:   { label: "Technology Company", tag: "Parent" },
    platform: { label: "Industrial Innovation Platform", tag: "Platform", motto: "We don't build every technology. We build the ecosystem that connects them." },
    health:   { label: "AI Healthcare",      tag: "Division" },
    energy:   { label: "Renewable Energy",   tag: "Division" },
    cta:      "Explore the ecosystem",
  },
  de: {
    eyebrow: "Unternehmensarchitektur",
    title: "Ein Ökosystem. Viele intelligente Lösungen.",
    lead: [
      { name: "BSGOTECH",        desc: "entwickelt intelligente digitale Ökosysteme." },
      { name: "NeuNexus",        desc: "integriert Technologien." },
      { name: "RENALIX",         desc: "transformiert das Gesundheitswesen." },
      { name: "NeuGreen Energy", desc: "treibt nachhaltige Infrastruktur an." },
    ],
    parent:   { label: "Technologieunternehmen", tag: "Muttergesellschaft" },
    platform: { label: "Industrielle Innovationsplattform", tag: "Plattform", motto: "Wir bauen nicht jede Technologie. Wir bauen das Ökosystem, das sie verbindet." },
    health:   { label: "KI-Gesundheit",     tag: "Division" },
    energy:   { label: "Erneuerbare Energie", tag: "Division" },
    cta:      "Ökosystem erkunden",
  },
  pt: {
    eyebrow: "Arquitectura Corporativa",
    title: "Um Ecossistema. Múltiplas Soluções Inteligentes.",
    lead: [
      { name: "BSGOTECH",        desc: "desenvolve ecossistemas digitais inteligentes." },
      { name: "NeuNexus",        desc: "integra tecnologias." },
      { name: "RENALIX",         desc: "transforma a saúde." },
      { name: "NeuGreen Energy", desc: "potencia infraestrutura sustentável." },
    ],
    parent:   { label: "Empresa Tecnológica", tag: "Empresa-mãe" },
    platform: { label: "Plataforma de Inovação Industrial", tag: "Plataforma", motto: "Não construímos toda a tecnologia. Construímos o ecossistema que as liga." },
    health:   { label: "Saúde com IA",       tag: "Divisão" },
    energy:   { label: "Energia Renovável",  tag: "Divisão" },
    cta:      "Explorar o ecossistema",
  },
};

const useE = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

// -------------------------------------------------------- Node

const Node = ({ tag, name, label, color, logo, Icon, delay = 0, testid, big = false, motto }) => (
  <motion.div
    initial={{ opacity: 0, y: 24, scale: 0.96 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ delay, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testid}
    className="relative rounded-xl overflow-hidden"
    style={{
      background: `linear-gradient(135deg, ${color}0f 0%, ${P.bg2} 55%, ${color}0a 100%)`,
      border: `1px solid ${color}55`,
      boxShadow: `0 0 40px ${color}22`,
      padding: big ? "32px 28px" : "24px 22px",
    }}
  >
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
      backgroundImage: `radial-gradient(circle at 20% 0%, ${color}22 0%, transparent 55%)`,
    }} />
    <div className="relative flex items-center gap-4">
      <motion.div
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: delay + 0.4 }}
        className="shrink-0 flex items-center justify-center"
        style={{
          width: big ? 64 : 48, height: big ? 64 : 48,
          borderRadius: 12,
          background: logo ? "rgba(255,255,255,0.96)" : `${color}18`,
          border: `1px solid ${color}66`,
          boxShadow: `0 0 28px ${color}55`,
        }}
      >
        {logo ? (
          <img src={logo} alt={`${name} logo`} className="object-contain" style={{ maxHeight: big ? 40 : 30, maxWidth: big ? 52 : 38 }} />
        ) : (
          Icon && <Icon size={big ? 28 : 22} style={{ color }} strokeWidth={1.6} />
        )}
      </motion.div>
      <div>
        <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color }}>{tag}</div>
        <div
          className="mt-1 font-semibold tracking-tight"
          style={{ color: "#fff", fontSize: big ? 24 : 18, letterSpacing: "-0.01em" }}
        >
          {name}
        </div>
        <div className="mt-0.5 text-[12px]" style={{ color: P.silver }}>{label}</div>
      </div>
    </div>
    {motto && (
      <div className="relative mt-4 pt-4 text-[12.5px] italic leading-relaxed"
        style={{ color: P.dim, borderTop: `1px solid ${color}22` }}>
        &ldquo;{motto}&rdquo;
      </div>
    )}
  </motion.div>
);

// Animated vertical connector with a running pulse dot
const VConnector = ({ color, delay = 0 }) => (
  <div className="relative flex flex-col items-center" aria-hidden="true">
    <motion.div
      initial={{ scaleY: 0 }}
      whileInView={{ scaleY: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.7, ease: "easeOut" }}
      className="w-px h-14 origin-top"
      style={{ background: `linear-gradient(180deg, ${color}00 0%, ${color}88 30%, ${color}88 70%, ${color}00 100%)` }}
    />
    <motion.div
      animate={{ y: [0, 42, 42], opacity: [0, 1, 0] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: delay + 0.6 }}
      className="absolute top-1"
      style={{
        width: 6, height: 6, borderRadius: 999,
        background: color, boxShadow: `0 0 12px ${color}, 0 0 24px ${color}66`,
      }}
    />
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: delay + 0.5, duration: 0.4 }}
    >
      <ChevronDown size={14} style={{ color, opacity: 0.75 }} />
    </motion.div>
  </div>
);

// Split branch (used to fork from NeuNexus into the two divisions)
const SplitBranch = ({ colorL, colorR }) => (
  <div className="relative w-full flex justify-center" aria-hidden="true">
    <svg width="100%" height="80" viewBox="0 0 400 80" preserveAspectRatio="none" style={{ maxWidth: 620 }}>
      <defs>
        <linearGradient id="glowL" x1="200" y1="0" x2="30" y2="70" gradientUnits="userSpaceOnUse">
          <stop offset="0%"  stopColor={colorL} stopOpacity="0" />
          <stop offset="50%" stopColor={colorL} stopOpacity="0.9" />
          <stop offset="100%" stopColor={colorL} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="glowR" x1="200" y1="0" x2="370" y2="70" gradientUnits="userSpaceOnUse">
          <stop offset="0%"  stopColor={colorR} stopOpacity="0" />
          <stop offset="50%" stopColor={colorR} stopOpacity="0.9" />
          <stop offset="100%" stopColor={colorR} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M200 0 C 200 40, 60 45, 30 78"
        stroke="url(#glowL)" strokeWidth="1.5" fill="none"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 1.0, ease: "easeOut" }}
      />
      <motion.path
        d="M200 0 C 200 40, 340 45, 370 78"
        stroke="url(#glowR)" strokeWidth="1.5" fill="none"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 1.0, ease: "easeOut" }}
      />
      {/* travelling dots along paths */}
      <motion.circle r="2.6" fill={colorL}
        style={{ filter: `drop-shadow(0 0 4px ${colorL})`, offsetPath: "path('M200 0 C 200 40, 60 45, 30 78')" }}
        animate={{ offsetDistance: ["0%", "100%"] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.circle r="2.6" fill={colorR}
        style={{ filter: `drop-shadow(0 0 4px ${colorR})`, offsetPath: "path('M200 0 C 200 40, 340 45, 370 78')" }}
        animate={{ offsetDistance: ["0%", "100%"] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      />
    </svg>
  </div>
);

// -------------------------------------------------------- Main section

export default function EcosystemArchitecture({ compact = false }) {
  const t = useE();
  return (
    <section
      id="ecosystem-architecture"
      data-testid="ecosystem-architecture"
      className="relative border-t"
      style={{ background: P.bg, borderColor: P.border }}
    >
      {/* atmospheric bg — strategic Nano Banana render + radial gradients */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: "url('/bsgotech/strategic/ecosystem-hero.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.18,
        maskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
      }} />
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 20% 15%, ${P.blue}18 0%, transparent 55%), radial-gradient(circle at 78% 82%, ${P.green}18 0%, transparent 55%), radial-gradient(circle at 50% 50%, ${P.purple}08 0%, transparent 60%)`,
      }} />

      <div className={`relative max-w-6xl mx-auto px-6 lg:px-10 ${compact ? "py-16 lg:py-20" : "py-20 lg:py-28"}`}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="max-w-3xl"
        >
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>
            {t.eyebrow}
          </div>
          <h2 className="mt-4 font-semibold tracking-tight text-[28px] sm:text-[36px] lg:text-[46px] leading-[1.05]"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.title}
          </h2>

          <div className="mt-6 space-y-1.5 text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
            {t.lead.map((l, i) => (
              <div key={i}>
                <span className="font-semibold" style={{ color: P.ink }}>{l.name}</span>{" "}
                <span>{l.desc}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Diagram */}
        <div className="mt-12 lg:mt-16 max-w-4xl mx-auto">
          {/* BSGOTECH parent */}
          <Node
            testid="eco-node-bsgotech"
            big
            tag={t.parent.tag}
            name="BSGOTECH"
            label={t.parent.label}
            color={P.white}
            logo="/bsgotech/logo-official.png"
            delay={0}
          />

          <VConnector color={P.silver} delay={0.15} />

          {/* NeuNexus platform */}
          <Node
            testid="eco-node-neunexus"
            big
            tag={t.platform.tag}
            name="NeuNexus"
            label={t.platform.label}
            color={P.green}
            logo="/neunexus/logo-official.png"
            delay={0.25}
            motto={t.platform.motto}
          />

          {/* Split into two divisions */}
          <div className="mt-6">
            <SplitBranch colorL={P.rose} colorR={P.teal} />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-2">
            <Node
              testid="eco-node-renalix"
              tag={t.health.tag}
              name="RENALIX"
              label={t.health.label}
              color={P.rose}
              logo="/renalix/logo-official.png"
              Icon={HeartPulse}
              delay={0.55}
            />
            <Node
              testid="eco-node-neugreen"
              tag={t.energy.tag}
              name="NeuGreen Energy"
              label={t.energy.label}
              color={P.teal}
              logo="/neugreen/logo-official.png"
              Icon={SunMedium}
              delay={0.65}
            />
          </div>
        </div>

        {!compact && (
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="mt-12 flex justify-center"
          >
            <Link
              to="/our-ecosystem"
              data-testid="ecosystem-explore-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
              style={{ border: `1px solid ${P.border}`, color: P.ink }}
            >
              {t.cta} <ArrowUpRight size={13} />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}

// Also export the tiny icon set for use elsewhere if needed
export { Layers };
